import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { support } from '../../content/support';
import { trackClick } from '../../firebase/analytics';

const safeTrackClick = (page, element) =>
  typeof trackClick === 'function' ? trackClick(page, element) : undefined;

function getPageIdentifier(pathname) {
  if (pathname === '/') return 'home';
  if (pathname === '/cv') return 'cv';
  if (pathname === '/apps') return 'apps';
  if (pathname === '/apps/birdify') return 'apps_birdify';
  if (pathname === '/apps/birdify/privacy') return 'apps_birdify_privacy';
  if (pathname === '/apps/numbcrunch') return 'apps_numbcrunch';
  if (pathname === '/apps/numbcrunch/privacy') return 'apps_numbcrunch_privacy';
  if (pathname === '/apps/qr-code') return 'apps_qr_code';
  if (pathname === '/privacy') return 'privacy';
  if (/^\/blog/.test(pathname)) return 'blog';
  return 'site';
}

const socials = [
  {
    href: 'https://www.linkedin.com/in/kevallangalia/',
    element: 'footer_linkedin_link',
    icon: 'fab fa-linkedin-in',
    label: 'LinkedIn',
  },
  {
    href: 'https://github.com/thekeval',
    element: 'footer_github_link',
    icon: 'fab fa-github',
    label: 'GitHub',
  },
  {
    href: 'https://x.com/kevallangalia',
    element: 'footer_x_twitter_link',
    icon: 'fa-brands fa-x-twitter',
    label: 'X',
  },
  {
    href: 'https://www.instagram.com/kevallangalia',
    element: 'footer_instagram_link',
    icon: 'fab fa-instagram',
    label: 'Instagram',
  },
  {
    href: 'https://stackoverflow.com/users/2049623/keval-langalia',
    element: 'footer_stackoverflow_link',
    icon: 'fab fa-stack-overflow',
    label: 'Stack Overflow',
  },
];

export default function SiteFooter({
  preferencesButtonRef,
  showChoices,
  onOpenPreferences,
  status = '',
  consent = null,
}) {
  const location = useLocation();
  const { pathname } = location;
  const page = getPageIdentifier(pathname);
  const showSupport = pathname === '/apps';

  // Minimal footer on the homepage
  if (pathname === '/') {
    return (
      <footer className="kds-home-footer" aria-label="Privacy and cookies">
        <div className="kds-home-footer__inner kds-cluster">
          <Link
            to="/privacy"
            className="kds-btn kds-btn--text kds-btn--sm"
            onClick={safeTrackClick('home', 'footer_privacy_link')}
          >
            Privacy
          </Link>
          <button
            type="button"
            className="kds-btn kds-btn--text kds-btn--sm"
            ref={preferencesButtonRef}
            aria-expanded={showChoices}
            aria-controls={showChoices ? 'analytics-preferences' : undefined}
            onClick={onOpenPreferences}
          >
            Cookies
          </button>
          {status ? (
            <span className="kds-body-sm kds-muted" role="status">
              {status}
            </span>
          ) : null}
        </div>
      </footer>
    );
  }

  return (
    <footer className="kds-site-footer" aria-label="Site footer">
      <div className="kds-site-footer__inner">
        <div className="kds-site-footer__grid">
          {/* Identity & Bio & Socials */}
          <div className="kds-site-footer__col kds-site-footer__col--brand">
            <Link
              to="/"
              className="kds-site-footer__brand"
              onClick={safeTrackClick(page, 'footer_brand_home_link')}
              aria-label="Keval Langalia - Back to Home"
            >
              <span className="kds-site-footer__brand-text">Keval Langalia</span>
              <span className="kds-site-footer__brand-dot" aria-hidden="true">.</span>
            </Link>
            <p className="kds-site-footer__tagline">
              Senior Software Engineer · Mobile & Web Apps
            </p>
            <p className="kds-site-footer__subtagline">
              Expert in all things Android & Automotive
            </p>
            <div className="kds-site-footer__contact">
              <a
                href="mailto:hello@keval.io"
                className="kds-site-footer__contact-link kds-link-underline"
                onClick={safeTrackClick(page, 'footer_email_link')}
              >
                <i className="fas fa-envelope" aria-hidden="true" />
                <span>hello@keval.io</span>
              </a>
              <span className="kds-site-footer__location">
                <i className="fas fa-location-dot" aria-hidden="true" />
                <span>Toronto, Canada</span>
              </span>
            </div>

            {/* Social media links with creative icon buttons */}
            <div className="kds-site-footer__socials" aria-label="Social media profiles">
              {socials.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="kds-site-footer__social-btn"
                  onClick={safeTrackClick(page, social.element)}
                  aria-label={social.label}
                  title={social.label}
                >
                  <i className={social.icon} aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {/* Navigation links */}
          <nav className="kds-site-footer__col" aria-label="Footer navigation">
            <h3 className="kds-site-footer__title">Navigation</h3>
            <ul className="kds-site-footer__links">
              <li>
                <Link to="/" onClick={safeTrackClick(page, 'footer_home_link')}>
                  <i className="fas fa-house" aria-hidden="true" />
                  <span>Home</span>
                </Link>
              </li>
              <li>
                <Link to="/cv" onClick={safeTrackClick(page, 'footer_cv_link')}>
                  <i className="fas fa-file-lines" aria-hidden="true" />
                  <span>CV</span>
                </Link>
              </li>
              <li>
                <Link to="/apps" onClick={safeTrackClick(page, 'footer_apps_link')}>
                  <i className="fas fa-briefcase" aria-hidden="true" />
                  <span>Portfolio</span>
                </Link>
              </li>
              <li>
                <Link to="/blog" onClick={safeTrackClick(page, 'footer_blog_link')}>
                  <i className="fas fa-feather-pointed" aria-hidden="true" />
                  <span>Blog & Notes</span>
                </Link>
              </li>
            </ul>
          </nav>

          {/* Privacy & Settings */}
          <nav className="kds-site-footer__col" aria-label="Privacy and legal">
            <h3 className="kds-site-footer__title">Privacy & Legal</h3>
            <ul className="kds-site-footer__links">
              <li>
                <Link to="/privacy" onClick={safeTrackClick(page, 'footer_privacy_link')}>
                  <i className="fas fa-shield-halved" aria-hidden="true" />
                  <span>Privacy</span>
                </Link>
              </li>
              <li>
                <a href="/licenses.html" onClick={safeTrackClick(page, 'footer_licenses_link')}>
                  <i className="fas fa-scale-balanced" aria-hidden="true" />
                  <span>Licenses</span>
                </a>
              </li>
              <li>
                <button
                  type="button"
                  className="kds-site-footer__pref-btn"
                  ref={preferencesButtonRef}
                  aria-expanded={showChoices}
                  aria-controls={showChoices ? 'analytics-preferences' : undefined}
                  onClick={onOpenPreferences}
                >
                  <i className="fas fa-cookie-bite" aria-hidden="true" />
                  <span>Cookies</span>
                </button>
              </li>
            </ul>

            <div className="kds-site-footer__status-wrap">
              <span className="kds-site-footer__status" role="status">
                {status ? (
                  <>
                    <span
                      className={`kds-site-footer__status-dot ${
                        consent === 'granted'
                          ? 'kds-site-footer__status-dot--granted'
                          : 'kds-site-footer__status-dot--denied'
                      }`}
                      aria-hidden="true"
                    />
                    <span>{status}</span>
                  </>
                ) : null}
              </span>
            </div>
          </nav>
        </div>

        {/* Contextual support offer when enabled on the route */}
        {showSupport ? (
          <div className="kds-site-footer__support">
            <i className={support.icon} aria-hidden="true" />
            <span>{support.footerNote}</span>
            <a
              href={support.href}
              target="_blank"
              rel="noopener noreferrer"
              className="kds-link kds-site-footer__support-link"
              onClick={safeTrackClick(page, support.element)}
            >
              {support.label}
            </a>
          </div>
        ) : null}

        {/* Bottom meta bar */}
        <div className="kds-site-footer__bottom">
          <p className="kds-site-footer__copyright">
            © {new Date().getFullYear()} Keval Langalia. All rights reserved.
          </p>
          <p className="kds-site-footer__craft">
            Crafted with care · React 18 & KDS · Deployed on Firebase
          </p>
        </div>
      </div>
    </footer>
  );
}
